// Email service - Uses backend API for sending emails via SendGrid

const BASE_URL = 'https://apps.acom.edu.au';
const API_URL = process.env.NODE_ENV === 'production' 
  ? 'https://apps.acom.edu.au/api' 
  : 'http://localhost:5000/api';

export const sendSubmitterConfirmation = async (request) => {
  try {
    const response = await fetch(`${API_URL}/emails/submitter-confirmation`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ request })
    });
    
    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.error || 'Failed to send email');
    }
    
    return result;
  } catch (error) {
    console.error('Error sending submitter confirmation:', error);
    // Don't throw error to prevent form submission from failing
    return { success: false, error: error.message };
  }
};

export const sendApproverNotification = async (request, approverEmail) => {
  try {
    const response = await fetch(`${API_URL}/emails/approver-notification`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ request, approverEmail })
    });
    
    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.error || 'Failed to send email');
    }
    
    return result;
  } catch (error) {
    console.error('Error sending approver notification:', error.message);
    // Don't throw error to prevent form submission from failing
    // Email failure shouldn't block the request submission
    return { success: false, error: error.message };
  }
};

export const sendStatusUpdateNotification = async (request, newStatus, notes) => {
  try {
    const response = await fetch(`${API_URL}/emails/status-update`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ request, newStatus, notes })
    });
    
    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.error || 'Failed to send email');
    }
    
    return result;
  } catch (error) {
    console.error('Error sending status update notification:', error);
    return { success: false, error: error.message };
  }
};

export const sendApprovalToAndres = async (request) => {
  try {
    const response = await fetch(`${API_URL}/emails/approval-notification-andres`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ request })
    });
    
    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.error || 'Failed to send email');
    }
    
    return result;
  } catch (error) {
    console.error('Error sending approval notification to Andres:', error);
    return { success: false, error: error.message };
  }
};

// Send notifications to secondary recipients after academic change approval
export const sendAcademicChangeApprovalNotifications = async (request, recipientEmails) => {
  try {
    const response = await fetch(`${API_URL}/emails/academic-change-approved`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ request, recipientEmails })
    });
    
    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.error || 'Failed to send email');
    }
    
    return result;
  } catch (error) {
    console.error('Error sending academic change approval notifications:', error);
    return { success: false, error: error.message };
  }
};

// Send comment notification
export const sendCommentNotification = async (request, comment, recipientEmail) => {
  try {
    const response = await fetch(`${API_URL}/emails/comment-notification`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ request, comment, recipientEmail })
    });
    
    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.error || 'Failed to send email');
    }
    
    return result;
  } catch (error) {
    console.error('Error sending comment notification:', error);
    return { success: false, error: error.message };
  }
};

// Send travel approval notification to info@acom.edu.au
export const sendTravelApprovalToInfo = async (request) => {
  try {
    const response = await fetch(`${API_URL}/emails/travel-approved-notification`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ request })
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || 'Failed to send email');
    }

    return result;
  } catch (error) {
    console.error('Error sending travel approval notification:', error);
    return { success: false, error: error.message };
  }
};

// Send marketing approval notification to hhildebrandt@acom.edu.au
export const sendMarketingApprovalToMarketing = async (request) => {
  try {
    const response = await fetch(`${API_URL}/emails/marketing-approved-notification`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ request })
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || 'Failed to send email');
    }

    return result;
  } catch (error) {
    console.error('Error sending marketing approval notification:', error);
    return { success: false, error: error.message };
  }
};

// Send leave approval notification to payroll@acom.edu.au
export const sendLeaveApprovalToPayroll = async (request) => {
  try {
    const response = await fetch(`${API_URL}/emails/leave-approved-notification`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ request })
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || 'Failed to send email');
    }

    return result;
  } catch (error) {
    console.error('Error sending leave approval notification:', error);
    return { success: false, error: error.message };
  }
};

// Helper function to generate email preview (for development)
export const generateEmailPreview = (type, data) => {
  const templates = {
    approverNotification: `
      Subject: Extension Request Pending Approval - ${data.studentName} (${data.studentNumber})
      To: ${data.approverEmail}
      
      A new extension request has been submitted and requires your review.
      
      Student: ${data.studentName}
      Student Number: ${data.studentNumber}
      Student Email: ${data.studentEmail}
      Award: ${data.award}
      SEM: ${data.sem}
      Units and Assignments: ${data.unitsAndAssignments}
      
      Review at: ${BASE_URL}/request/${data.id}
    `,
    statusUpdate: `
      Subject: Extension Request ${data.status} - ${data.studentName}
      To: ${data.submittedBy}
      
      Your extension request has been ${data.status}.
      
      ${data.notes ? `Notes: ${data.notes}` : ''}
      
      View at: ${BASE_URL}/request/${data.id}
    `,
    approvalToAndres: `
      Subject: Extension Request Approved - ${data.studentName}
      To: andres@nerdsontime.com.au
      
      An extension request has been approved.
      
      All request details included...
      View at: ${BASE_URL}/request/${data.id}
    `
  };
  
  return templates[type] || 'Email template not found';
};